import React, {useEffect, useState} from 'react';
import {Controller} from "react-hook-form";
import debounce from 'lodash/debounce'
import {trafficSourceServices} from "../../service/trafficSourceService";
import {Autocomplete, Box, FormLabel, TextField, Tooltip, Typography} from "@mui/material";
import {BoltOutlined, Info} from "@mui/icons-material";
import {CustomTooltip} from "../landings/LandingForm";

function TrafficSourceSelect(
    {
        control,
        updateFunc = null,
        showFormLabel = true,
        showInputLabel = false,
        validate = null,
        slotProps,
        workspace = ""
    }
) {

    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [requestError, setRequestError] = useState('');

    useEffect(() => {
        async function fetchData(): void {
            try {
                setLoading(true);
                setRequestError('');
                const response = await trafficSourceServices.read({name: search, workspace});
                setOptions(
                    response.traffics.map(item => {
                        return {
                            value: item?.id,
                            label: item?.name,
                            custom1: item?.custom1Name,
                            custom2: item?.custom2Name,
                            custom3: item?.custom3Name,
                            custom4: item?.custom4Name,
                            custom5: item?.custom5Name,
                            custom6: item?.custom6Name,
                            custom7: item?.custom7Name,
                            custom8: item?.custom8Name,
                            custom9: item?.custom9Name,
                            custom10: item?.custom10Name,
                            integrationId: item?.integrationId,
                            workspaceId: item?.workspaceId,
                        }
                    })
                );
            } catch (e) {
                setRequestError('Unable to load traffic sources. Close and reopen the list to retry.');
            } finally {
                setLoading(false);
            }
        }

        if (open && (search.length === 0 || search.length > 0)) {
            fetchData();
        }

    }, [workspace, search, open]);

    useEffect(() => {
        if (!open) {
            setOptions([]);
            setSearch("");
        }
    }, [open]);

    const handleSearch = debounce(searchTerm => {
        setSearch(searchTerm);
    }, 800);

    return (
        <Controller
            render={({field: {onChange, ...props}, fieldState}) => (
                <Autocomplete
                    slotProps={slotProps}
                    id="traffic-source-select"
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    getOptionLabel={(option) => option?.label}
                    options={options}
                    loading={loading}
                    disableClearable
                    renderOption={(props, option, {index}) => (
                        <li {...props} key={index}>
                            {option?.integrationId > 0 && (
                                <>
                                    <Tooltip title="Integrated" aria-label="integrated">
                                        <BoltOutlined
                                            sx={{fontSize: 20, color: 'primary.main'}}
                                        />
                                    </Tooltip>
                                </>
                            )}
                            {option?.label}
                        </li>
                    )}
                    renderInput={params => (
                        <>
                            {showFormLabel && (
                                <FormLabel component="legend">
                                    <Box display="flex" flexDirection="row">
                                        <Box>
                                            <Typography variant="body1">
                                                Traffic Source
                                            </Typography>
                                        </Box>
                                        <Box mt={0.2}>
                                            <CustomTooltip
                                                disableFocusListener
                                                title="Select traffic source to automatically append query parameters to the campaign URL."
                                            >
                                                <Info color={"primary"} fontSize={"small"}/>
                                            </CustomTooltip>
                                        </Box>
                                    </Box>
                                </FormLabel>
                            )}
                            <TextField
                                autoFocus
                                {...params}
                                error={!!fieldState.error || !!requestError}
                                helperText={fieldState.error?.message || requestError || undefined}
                                variant="outlined"
                                size="small"
                                margin="dense"
                                placeholder="Select a traffic source"
                                value={search}
                                onChange={e => handleSearch(e.target.value)}
                                label={showInputLabel && "Traffic Source*"}
                                slotProps={{inputLabel: {shrink: true}}}
                            />
                        </>
                    )}
                    onChange={(e, data) => {
                        setRequestError('');
                        if (updateFunc != null) {
                            updateFunc(data);
                        }
                        onChange(data);
                    }}
                    {...props}
                />
            )}
            rules={{
                required: "Traffic source is required.",
                validate: validate || undefined,
            }}
            name="trafficSource"
            control={control}
            defaultValue={null}
        />
    );
}

export default TrafficSourceSelect;
